import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { InsightIA } from '@/hooks/useDashboard';

interface Props {
  insights: InsightIA[];
  loading?: boolean;
  onOpenAiDrawer?: () => void;
}

// Mapeamento tone → classes de fundo/borda para o cartão individual
const TONE_CARD: Record<InsightIA['tone'], string> = {
  warning: 'bg-warning-soft dark:bg-warning-soft-dark border-[rgba(180,115,10,0.30)] dark:border-[rgba(242,195,102,0.30)]',
  info:    'bg-info-soft dark:bg-info-soft-dark border-[rgba(43,95,168,0.25)] dark:border-[rgba(159,189,230,0.30)]',
  success: 'bg-success-soft dark:bg-success-soft-dark border-[rgba(30,138,60,0.25)] dark:border-[rgba(155,217,173,0.30)]',
  danger:  'bg-danger-soft dark:bg-danger-soft-dark border-[rgba(196,50,28,0.25)] dark:border-[rgba(240,162,152,0.30)]',
};

export function InsightsIACard({ insights, loading, onOpenAiDrawer }: Props) {
  const navigate = useNavigate();

  const handleCta = (insight: InsightIA) => {
    if (insight.cta.toLowerCase().includes('análise ia') || insight.cta.toLowerCase().includes('analise ia')) {
      onOpenAiDrawer?.();
    } else if (insight.action) {
      navigate(insight.action);
    }
  };

  return (
    <section
      aria-labelledby="insights-titulo"
      className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card shadow-sm-warm dark:shadow-sm-dark overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-[18px] pt-4 pb-3 border-b border-line dark:border-line-dark">
        <Sparkles className="w-4 h-4 text-brand-500" />
        <h3 id="insights-titulo" className="text-[14px] font-semibold text-ink-title dark:text-ink-title-dark">
          Insights da IA
        </h3>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2.5 p-[14px] flex-1">
        {loading ? (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className="p-3 rounded-[10px] border border-line dark:border-line-dark">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="mt-2 h-4 w-48" />
                <Skeleton className="mt-1 h-3 w-full" />
                <Skeleton className="mt-2 h-3 w-24" />
              </div>
            ))}
          </>
        ) : insights.length === 0 ? (
          <div className="py-8 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Nenhum insight relevante agora.
          </div>
        ) : (
          insights.map((ins, i) => (
            <div
              key={i}
              className={['p-3 rounded-[10px] border', TONE_CARD[ins.tone]].join(' ')}
            >
              <Badge tone={ins.tone} dot>{ins.badgeLabel}</Badge>
              <div className="mt-1.5 text-[13px] font-semibold text-ink-title dark:text-ink-title-dark leading-snug">
                {ins.title}
              </div>
              <div className="mt-0.5 text-[12px] leading-[1.45] text-ink-text dark:text-ink-text-dark">
                {ins.text}
              </div>
              <button
                type="button"
                onClick={() => handleCta(ins)}
                className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200 transition-colors"
              >
                {ins.cta} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
